'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Route {
  from: string;
  fromCode: string;
  to: string;
  toCode: string;
  price: string;
  airline: string;
  duration: string;
  popular: boolean;
  trend: 'hot' | 'rising' | 'deal';
  bookings: number;
}

const POPULAR_ROUTES: Route[] = [
  { from: 'London', fromCode: 'LHR', to: 'Dubai', toCode: 'DXB', price: '£289', airline: 'Emirates', duration: '7h', popular: true, trend: 'hot', bookings: 47 },
  { from: 'London', fromCode: 'LHR', to: 'New York', toCode: 'JFK', price: '£399', airline: 'British Airways', duration: '8h', popular: true, trend: 'hot', bookings: 38 },
  { from: 'Manchester', fromCode: 'MAN', to: 'Islamabad', toCode: 'ISB', price: '£449', airline: 'PIA', duration: '8h 30m', popular: true, trend: 'rising', bookings: 62 },
  { from: 'London', fromCode: 'LGW', to: 'Lahore', toCode: 'LHE', price: '£469', airline: 'Qatar Airways', duration: '9h', popular: true, trend: 'hot', bookings: 55 },
  { from: 'Birmingham', fromCode: 'BHX', to: 'Karachi', toCode: 'KHI', price: '£499', airline: 'Turkish Airlines', duration: '10h', popular: false, trend: 'deal', bookings: 29 },
  { from: 'London', fromCode: 'LHR', to: 'Toronto', toCode: 'YYZ', price: '£459', airline: 'Air Canada', duration: '9h', popular: true, trend: 'rising', bookings: 33 },
  { from: 'London', fromCode: 'LHR', to: 'Bangkok', toCode: 'BKK', price: '£519', airline: 'Thai Airways', duration: '11h', popular: false, trend: 'deal', bookings: 21 },
  { from: 'London', fromCode: 'LHR', to: 'Sydney', toCode: 'SYD', price: '£799', airline: 'Qantas', duration: '22h', popular: true, trend: 'hot', bookings: 18 },
];

const trendConfig = {
  hot: { label: '🔥 HOT', bg: 'bg-red-500', text: 'text-white', flash: true },
  rising: { label: '📈 RISING', bg: 'bg-amber-500', text: 'text-white', flash: true },
  deal: { label: '💰 DEAL', bg: 'bg-trust-green', text: 'text-white', flash: false },
};

export default function PopularRoutes() {
  const [flashState, setFlashState] = useState(true);
  const [liveBookings, setLiveBookings] = useState<Record<number, number>>({});

  // Flash indicator toggle
  useEffect(() => {
    const interval = setInterval(() => {
      setFlashState(prev => !prev);
    }, 800);
    return () => clearInterval(interval);
  }, []);

  // Simulate live booking counts incrementing
  useEffect(() => {
    const interval = setInterval(() => {
      const randomIdx = Math.floor(Math.random() * POPULAR_ROUTES.length);
      setLiveBookings(prev => ({
        ...prev,
        [randomIdx]: (prev[randomIdx] || 0) + 1,
      }));
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleBookRoute = (route: Route) => {
    const params = new URLSearchParams({
      type: 'flight',
      from: route.fromCode,
      to: route.toCode,
      airline: route.airline,
    });
    window.location.href = `/flight-search?${params.toString()}`;
  };

  return (
    <section className="py-16 bg-gradient-to-b from-surface to-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 rounded-full mb-4">
            <span className={`w-2.5 h-2.5 rounded-full bg-red-500 transition-opacity duration-300 ${flashState ? 'opacity-100' : 'opacity-20'}`}></span>
            <span className="text-sm font-black text-red-600">LIVE POPULAR ROUTES</span>
            <span className={`w-2.5 h-2.5 rounded-full bg-red-500 transition-opacity duration-300 ${flashState ? 'opacity-20' : 'opacity-100'}`}></span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-black text-foreground mb-3">
            Most Booked Routes
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Real-time popular routes from UK airports — updated live
          </p>
        </div>

        {/* Routes Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {POPULAR_ROUTES.map((route, idx) => {
            const trend = trendConfig[route.trend];
            const extraBookings = liveBookings[idx] || 0;
            const totalBookings = route.bookings + extraBookings;

            return (
              <div
                key={idx}
                className="bg-white rounded-2xl border-2 border-border hover:border-primary/40 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden group cursor-pointer"
                onClick={() => handleBookRoute(route)}
              >
                {/* Trend Badge */}
                <div className={`px-3 py-1.5 flex items-center justify-between ${trend.bg}`}>
                  <span className={`text-xs font-black ${trend.text}`}>{trend.label}</span>
                  {trend.flash && (
                    <span className="flex items-center gap-1">
                      <span className={`w-1.5 h-1.5 rounded-full bg-white transition-opacity duration-300 ${flashState ? 'opacity-100' : 'opacity-30'}`}></span>
                      <span className={`w-1.5 h-1.5 rounded-full bg-white transition-opacity duration-300 ${flashState ? 'opacity-30' : 'opacity-100'}`}></span>
                    </span>
                  )}
                </div>

                <div className="p-4">
                  {/* Route */}
                  <div className="flex items-center gap-2 mb-3">
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground">{route.from}</p>
                      <p className="font-black text-foreground text-sm">{route.fromCode}</p>
                    </div>
                    <div className="flex-1 flex items-center gap-1">
                      <div className="flex-1 h-px bg-border"></div>
                      <Icon name="PaperAirplaneIcon" size={14} className="text-primary rotate-0" />
                      <div className="flex-1 h-px bg-border"></div>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground">{route.to}</p>
                      <p className="font-black text-foreground text-sm">{route.toCode}</p>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="text-center mb-3">
                    <p className="text-2xl font-black text-primary">{route.price}</p>
                    <p className="text-xs text-muted-foreground">from per person</p>
                  </div>

                  {/* Details */}
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                    <span className="flex items-center gap-1">
                      <Icon name="ClockIcon" size={12} />
                      {route.duration}
                    </span>
                    <span className="font-bold text-foreground truncate max-w-[100px]">{route.airline}</span>
                  </div>

                  {/* Live bookings counter */}
                  <div className="flex items-center gap-1.5 bg-red-50 rounded-lg px-2 py-1.5 mb-3">
                    <span className={`w-2 h-2 rounded-full bg-red-500 flex-shrink-0 transition-opacity duration-300 ${flashState ? 'opacity-100' : 'opacity-30'}`}></span>
                    <span className="text-xs font-bold text-red-600">{totalBookings} booked today</span>
                  </div>

                  {/* Book Button */}
                  <button className="w-full py-2.5 bg-gradient-to-r from-primary to-secondary text-white text-sm font-black rounded-xl hover:shadow-md transition-all group-hover:scale-[1.02] active:scale-95 touch-manipulation">
                    Search This Route
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-8">
          <a
            href="/flight-search"
            className="inline-flex items-center gap-2 px-6 py-3 border-2 border-primary text-primary font-black rounded-xl hover:bg-primary hover:text-white transition-all"
          >
            <Icon name="MagnifyingGlassIcon" size={18} />
            Search All Routes
          </a>
        </div>
      </div>
    </section>
  );
}
